import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Calendar as CalendarIcon,
  CheckCircle2,
  Wrench,
  Briefcase,
  Plus,
  ArrowRight,
  Navigation,
  PlayCircle,
  PackageCheck,
} from 'lucide-react';
import { format, parseISO, formatDistanceToNow } from 'date-fns';
import { useAuth } from '../lib/auth';
import { useJobStore, type Job } from '../lib/store';
import { isJobMine } from '../lib/utils';
import StatCard from '../components/StatCard';
import JobCard from '../components/JobCard';
import EmptyState from '../components/EmptyState';
import StatusBadge from '../components/StatusBadge';
import TeamStatus from '../components/TeamStatus';
import BookFollowUpModal from '../components/BookFollowUpModal';

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
}

export default function Dashboard() {
  const { user, isAdmin } = useAuth();
  const { jobs, fetchJobs } = useJobStore();
  const navigate = useNavigate();

  useEffect(() => {
    fetchJobs('All', 'All');
  }, [fetchJobs]);

  const todayStr = format(new Date(), 'yyyy-MM-dd');

  const buckets = useMemo(() => {
    const mine = isAdmin ? jobs : jobs.filter((j) => isJobMine(j, user));
    const todayAll = mine.filter((j) => j.date === todayStr && j.status !== 'Invoiced');
    const currentJob = todayAll.find((j) => j.status === 'In Progress') || todayAll[0] || null;
    const upNext = todayAll.filter((j) => j !== currentJob);
    const upcoming = mine
      .filter((j) => j.date > todayStr && !['Invoiced', 'Completed'].includes(j.status))
      .sort((a, b) => (a.date < b.date ? -1 : 1))
      .slice(0, 8);
    const awaitingParts = jobs.filter((j) => j.status === 'Awaiting Parts');
    const needsFollowUp = jobs
      .filter((j) => j.status === 'Follow-up Visit Required')
      .sort((a, b) => {
        // Newest parts-received first
        const at = a.parts_last_received_at || a.updated_at || '';
        const bt = b.parts_last_received_at || b.updated_at || '';
        return bt.localeCompare(at);
      });
    const readyToInvoice = jobs.filter((j) => j.status === 'Ready to Invoice');
    const inProgress = jobs.filter((j) => j.status === 'In Progress');
    return { mine, todayAll, currentJob, upNext, upcoming, awaitingParts, needsFollowUp, readyToInvoice, inProgress };
  }, [jobs, user, isAdmin, todayStr]);

  // Book-follow-up modal state (admin only)
  const [followUpJob, setFollowUpJob] = useState<Job | null>(null);

  return (
    <div className="space-y-8">
      {/* Premium hero */}
      <div
        className="relative overflow-hidden rounded-2xl p-6 sm:p-8 text-white flex flex-wrap items-end justify-between gap-4"
        style={{
          background:
            'linear-gradient(120deg, #0a3b7a 0%, #082f61 55%, #041a37 100%)',
        }}
      >
        <div
          className="absolute inset-0 opacity-[0.08]"
          style={{
            backgroundImage:
              'radial-gradient(circle at 20% 20%, white 1px, transparent 1px), radial-gradient(circle at 60% 70%, white 1px, transparent 1px)',
            backgroundSize: '40px 40px',
          }}
        />
        <div className="relative">
          <div className="text-sm font-semibold text-white/70">{greeting()},</div>
          <h1 className="text-3xl sm:text-4xl font-bold mt-0.5">
            {user?.full_name?.split(' ')[0] || user?.username}
          </h1>
          <div className="text-sm text-white/70 mt-2">
            {format(new Date(), 'EEEE, d MMMM yyyy')} · {isAdmin ? 'Admin' : 'Field engineer'}
          </div>
        </div>
        <div className="relative flex items-center gap-3">
          {isAdmin && (
            <button
              onClick={() => navigate('/jobs/new')}
              className="btn btn-lg bg-white text-brand-700 hover:bg-brand-50 shadow-sm"
              data-testid="new-job-button"
            >
              <Plus className="h-5 w-5" /> New Job
            </button>
          )}
        </div>
      </div>

      {isAdmin ? (
        <AdminView
          buckets={buckets}
          onNav={(p: string) => navigate(p)}
          allJobs={jobs}
          todayStr={todayStr}
          onBookFollowUp={(job: Job) => setFollowUpJob(job)}
        />
      ) : (
        <WorkerView buckets={buckets} onNav={(p: string) => navigate(p)} />
      )}

      {followUpJob && (
        <BookFollowUpModal
          job={followUpJob}
          open={!!followUpJob}
          onClose={() => setFollowUpJob(null)}
          onBooked={() => {
            setFollowUpJob(null);
            fetchJobs('All', 'All');
          }}
        />
      )}
    </div>
  );
}

function AdminView({ buckets, onNav, allJobs, todayStr, onBookFollowUp }: any) {
  return (
    <>
      {/* Parts arrived — needs re-booking (highest priority alert) */}
      {buckets.needsFollowUp.length > 0 && (
        <PartsArrivedBanner jobs={buckets.needsFollowUp} onBook={onBookFollowUp} onNav={onNav} />
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <StatCard
          icon={CalendarIcon}
          label="Today"
          value={buckets.todayAll.length}
          hint={buckets.inProgress.length ? `${buckets.inProgress.length} in progress` : 'jobs scheduled'}
          tone="brand"
          onClick={() => onNav('/calendar')}
        />
        <StatCard
          icon={CheckCircle2}
          label="Ready to Invoice"
          value={buckets.readyToInvoice.length}
          hint="awaiting billing"
          tone="purple"
        />
        <StatCard
          icon={Wrench}
          label="Awaiting Parts"
          value={buckets.awaitingParts.length}
          hint={buckets.awaitingParts.length ? 'jobs waiting' : 'all clear'}
          tone="orange"
          onClick={() => onNav('/parts-orders?status=Ordered')}
        />
        <StatCard
          icon={Briefcase}
          label="Pipeline"
          value={buckets.mine.filter((j: Job) => j.status !== 'Invoiced').length}
          hint="active jobs"
          tone="emerald"
        />
      </div>

      {/* Today + Team */}
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <Section
            title={`Today's schedule · ${buckets.todayAll.length}`}
            actionLabel={buckets.todayAll.length > 4 ? 'View calendar' : undefined}
            onAction={() => onNav('/calendar')}
          >
            {buckets.todayAll.length === 0 ? (
              <div className="card p-6">
                <EmptyState
                  icon={CalendarIcon}
                  title="No jobs today"
                  subtitle="You're all clear for today."
                />
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-3">
                {buckets.todayAll.slice(0, 6).map((job: Job) => (
                  <JobCard key={job.id} job={job} />
                ))}
              </div>
            )}
          </Section>
        </div>
        <div>
          <TeamStatus jobs={allJobs} today={todayStr} />
        </div>
      </div>

      {/* Ready to invoice */}
      {buckets.readyToInvoice.length > 0 && (
        <Section title={`Ready to invoice · ${buckets.readyToInvoice.length}`}>
          <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3">
            {buckets.readyToInvoice.slice(0, 6).map((job: Job) => (
              <JobCard key={job.id} job={job} />
            ))}
          </div>
        </Section>
      )}

      {/* Upcoming */}
      {buckets.upcoming.length > 0 && (
        <Section
          title={`Upcoming · ${buckets.upcoming.length}`}
          actionLabel="See all"
          onAction={() => onNav('/jobs')}
        >
          <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3">
            {buckets.upcoming.slice(0, 6).map((job: Job) => (
              <JobCard key={job.id} job={job} />
            ))}
          </div>
        </Section>
      )}
    </>
  );
}

function WorkerView({ buckets, onNav }: any) {
  const current: Job | null = buckets.currentJob;
  return (
    <>
      {current ? (
        <div
          className="relative overflow-hidden rounded-2xl p-6 sm:p-8 text-white"
          style={{
            background:
              'linear-gradient(135deg, #0a3b7a 0%, #041a37 100%)',
          }}
        >
          <div className="flex items-center gap-2 mb-3">
            <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-white/15 text-[11px] font-bold uppercase tracking-wider">
              Current Job
            </span>
            <StatusBadge status={current.status} size="sm" />
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold leading-tight">{current.title}</h2>
          {current.client_name && (
            <div className="text-white/80 mt-1">{current.client_name}</div>
          )}
          {(current.location?.address || current.client_address) && (
            <div className="text-white/70 text-sm mt-2">
              {current.location?.address || current.client_address}
            </div>
          )}
          <div className="flex flex-wrap gap-3 mt-6">
            <button onClick={() => onNav(`/jobs/${current.id}`)} className="btn bg-white text-brand-700 hover:bg-brand-50 btn-lg">
              <PlayCircle className="h-5 w-5" />
              {current.status === 'In Progress' ? 'Continue' : 'Open Job'}
            </button>
            {(current.location?.address || current.client_address) && (
              <a
                href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
                  current.location?.address || current.client_address || ''
                )}`}
                target="_blank"
                rel="noreferrer"
                className="btn bg-white/10 text-white hover:bg-white/20 btn-lg"
              >
                <Navigation className="h-5 w-5" />
                Directions
              </a>
            )}
          </div>
        </div>
      ) : (
        <div className="card p-8">
          <EmptyState
            icon={CheckCircle2}
            title="You're all caught up"
            subtitle="No jobs scheduled for you today."
          />
        </div>
      )}

      {buckets.upNext.length > 0 && (
        <Section title={`Up next today · ${buckets.upNext.length}`}>
          <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3">
            {buckets.upNext.map((job: Job) => (
              <JobCard key={job.id} job={job} />
            ))}
          </div>
        </Section>
      )}

      {buckets.upcoming.length > 0 && (
        <Section title="Coming up this week" actionLabel="See all" onAction={() => onNav('/jobs')}>
          <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3">
            {buckets.upcoming.slice(0, 6).map((job: Job) => (
              <JobCard key={job.id} job={job} />
            ))}
          </div>
        </Section>
      )}
    </>
  );
}

function PartsArrivedBanner({
  jobs,
  onBook,
  onNav,
}: {
  jobs: Job[];
  onBook: (j: Job) => void;
  onNav: (p: string) => void;
}) {
  const count = jobs.length;
  return (
    <section
      className="relative rounded-2xl bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 p-5 sm:p-6 shadow-card overflow-hidden"
      data-testid="parts-arrived-banner"
    >
      <div className="flex items-start gap-4">
        <div className="w-11 h-11 rounded-xl bg-amber-500 text-white grid place-items-center shrink-0 shadow-sm">
          <PackageCheck className="h-6 w-6" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="font-display font-bold text-lg text-amber-900">
              Parts arrived — {count} job{count === 1 ? '' : 's'} to re-book
            </h2>
            <span className="chip bg-amber-500/15 text-amber-900 ring-1 ring-amber-500/25">
              Action required
            </span>
          </div>
          <p className="text-sm text-amber-800/80 mt-1">
            Parts are in stock. Pick a follow-up date and assign a worker.
          </p>
        </div>
      </div>

      <div className="mt-4 grid gap-2">
        {jobs.slice(0, 4).map((job) => {
          const receivedAgo = job.parts_last_received_at
            ? formatDistanceToNow(parseISO(job.parts_last_received_at), { addSuffix: true })
            : 'recently';
          return (
            <div
              key={job.id}
              className="bg-white/90 backdrop-blur rounded-lg border border-amber-100 p-3 sm:p-3.5 flex flex-wrap items-center gap-3"
              data-testid={`parts-arrived-row-${job.id}`}
            >
              <button
                onClick={() => onNav(`/jobs/${job.id}`)}
                className="min-w-0 flex-1 text-left group"
              >
                <div className="flex items-center gap-2 flex-wrap">
                  {job.job_number && (
                    <span className="text-[10px] font-bold text-slate-400 tracking-wider">
                      {job.job_number}
                    </span>
                  )}
                  <span className="font-semibold text-slate-900 truncate group-hover:text-brand-700">
                    {job.client_name || job.title}
                  </span>
                </div>
                <div className="text-xs text-slate-500 mt-0.5 truncate">
                  {job.title} · Parts received {receivedAgo}
                </div>
              </button>
              <button
                onClick={() => onBook(job)}
                className="btn btn-primary shrink-0"
                data-testid={`book-followup-btn-${job.id}`}
              >
                <CalendarIcon className="h-4 w-4" />
                Book follow-up
              </button>
            </div>
          );
        })}
        {jobs.length > 4 && (
          <button
            onClick={() => onNav('/jobs?status=Follow-up+Visit+Required')}
            className="text-sm font-semibold text-amber-900 hover:text-amber-950 inline-flex items-center gap-1 mt-1"
          >
            View all {jobs.length} <ArrowRight className="h-4 w-4" />
          </button>
        )}
      </div>
    </section>
  );
}

function Section({
  title,
  actionLabel,
  onAction,
  children,
}: {
  title: string;
  actionLabel?: string;
  onAction?: () => void;
  children: React.ReactNode;
}) {
  return (
    <section>
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-bold text-slate-900">{title}</h2>
        {actionLabel && (
          <button
            onClick={onAction}
            className="text-sm font-semibold text-brand-700 hover:text-brand-800 inline-flex items-center gap-1"
          >
            {actionLabel}
            <ArrowRight className="h-4 w-4" />
          </button>
        )}
      </div>
      {children}
    </section>
  );
}
